import { Box, Avatar, Button, Card, Text } from "@pushpress/shared-components";
import { Col, Container, Row } from "reactstrap";
import styles from "./BookDemo.module.scss";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";
import salesAvatar from "../../../assets/img/avatars/beth-propic.png";
import { GetStartedEvent } from "../events";
import { useExperiment } from "../../../hooks/useExperiment";
import {
  useRevenueHero,
  BookDemoFormData,
} from "../../../hooks/useRevenueHero";
import { AppContext } from "../../../context/app";
import { useContext } from "react";
import { toast } from "react-toastify";
import { defaultToastOptions } from "../../../utils/misc";

function BookDemo() {
  const app = useContext(AppContext);
  const { client, user } = app;
  const routerId = process.env.REVENUE_HERO_GET_STARTED_ROUTER_ID;
  const { showScheduler } = useRevenueHero(routerId);
  const { useExperimentValue } = useExperiment();
  const { trackEvent } = useAnalytics();
  const isInsuranceExperimentEnabled = useExperimentValue(
    "pushpress_insurance_banner",
  );

  const bookDemoColumnSizes = isInsuranceExperimentEnabled
    ? { lg: 12, xl: 6 }
    : { sm: 12, md: 4 };

  const handleRevHeroClick = async () => {
    if (!client?.email || !client?.company) {
      trackEvent(GetStartedEvent.BookDemoFailed, {
        error: "No email or company",
        clientUuid: client?.uuid,
        company: client?.company,
      });
      return;
    }

    try {
      trackEvent(GetStartedEvent.BookDemoClicked);
      await showScheduler<BookDemoFormData>({
        email: client?.email,
        firstname: user?.first_name,
        lastname: user?.last_name,
        company: client?.company,
        countryName: client?.countryName,
        gymType: client?.gymType,
        utmSource: "core-cp",
        utmMedium: "product",
        utmCampaign: "get_started",
      });
    } catch (error) {
      trackEvent(GetStartedEvent.BookDemoFailed, {
        error: "Unable to book revenue hero demo - check revenue hero logs",
        clientUuid: client?.uuid,
        routerId,
      });

      toast.error(
        "Sorry, we're experiencing issues with booking a demo. Please try again later.",
        defaultToastOptions,
      );
    }
  };

  const bookDemoCard = (
    <Card className={"p-4"} style={{ minWidth: 200, maxWidth: 350 }}>
      <div style={{ display: "flex" }}>
        <Avatar size="large">
          <img src={salesAvatar} alt="sales profile pic" />
        </Avatar>
        <div style={{ marginLeft: "1rem" }}>
          <Text style={{ marginLeft: 10 }} variant="heading-5">
            Looking to Upgrade?
          </Text>
          <Text style={{ marginLeft: 10 }} variant="body-md" color="wolf-600">
            Explore all the benefits of our pro or max tiers.
          </Text>
        </div>
      </div>
      <div style={{ marginTop: "1rem" }}>
        <Button
          fullWidth
          text={"Talk to a sales specialist today"}
          onClick={handleRevHeroClick}
        />
      </div>
    </Card>
  );

  return (
    <Box
      style={{ backgroundColor: "hsla(206, 100%, 95%, 1)", flex: 2 }}
      paddingSize="medium"
    >
      <Container>
        <Row>
          <Col className="d-flex justify-content-between flex-column">
            <Row className="no-gutters" style={{ paddingRight: "1rem" }}>
              <p className="tw-max-w-md">
                Unlock the power of your new system! Explore our setup guide or
                book a demo with one of our sales specialists to take things to
                the next level.
              </p>
            </Row>
            <Row>
              <Container>
                <Row className="no-gutters">
                  <Text variant="heading-5">Helpful Resources</Text>
                </Row>
                <Row className="no-gutters">
                  <div className={styles.helpLinks}>
                    <a
                      href="https://help.pushpress.com/en/articles/6446620-contact-us-hi-how-can-we-help"
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => {
                        trackEvent(GetStartedEvent.HelpResourceClicked, {
                          resource: "How To Find Help",
                        });
                      }}
                    >
                      How To Find Help
                    </a>

                    <a
                      href="https://help.pushpress.com/en/"
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => {
                        trackEvent(GetStartedEvent.HelpResourceClicked, {
                          resource: "Help Articles",
                        });
                      }}
                    >
                      Help Articles
                    </a>
                  </div>
                </Row>
              </Container>
            </Row>
          </Col>
          <Col {...bookDemoColumnSizes}>
            <div
              className="tw-mt-4 xl:tw-mt-0"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
              }}
            >
              {bookDemoCard}
            </div>
          </Col>
        </Row>
      </Container>
    </Box>
  );
}

export default BookDemo;
